import { supabase } from './supabase';

/**
 * Cliente del endpoint api/admin-users.
 *
 * Las altas, bajas y cambios de contraseña requieren la service role key, que
 * no puede salir del servidor. Desde aquí solo se envía el JWT de la sesión
 * actual; el servidor comprueba que quien firma sea admin o director.
 */

function baseUrl(): string {
  const explicita = import.meta.env.VITE_ADMIN_API_URL as string | undefined;
  if (explicita) return explicita.replace(/\/+$/, '');

  // En la app empaquetada la página se sirve por file://, donde una petición
  // relativa no tiene origen al que dirigirse: hace falta URL absoluta.
  if (window.location.protocol === 'file:') {
    const front = import.meta.env.VITE_FRONTEND_URL as string | undefined;
    if (!front) {
      throw new Error(
        'Falta VITE_FRONTEND_URL (o VITE_ADMIN_API_URL): la aplicación de escritorio ' +
        'necesita la dirección del servidor para administrar usuarios.'
      );
    }
    return front.replace(/\/+$/, '');
  }

  return ''; // en web, mismo origen
}

type Accion = 'create' | 'update' | 'setPassword' | 'delete';

export async function adminUsers<T = unknown>(action: Accion, payload: Record<string, unknown>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Tu sesión ha caducado. Vuelve a entrar.');

  let respuesta: Response;
  try {
    respuesta = await fetch(`${baseUrl()}/api/admin-users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, payload }),
    });
  } catch {
    throw new Error('No se ha podido contactar con el servidor de administración.');
  }

  const cuerpo = await respuesta.json().catch(() => ({} as Record<string, string>));
  if (!respuesta.ok) throw new Error(cuerpo.error || `Error ${respuesta.status}`);
  return cuerpo as T;
}
